import { toast } from 'sonner';
import { notifyError } from '@/libs/bugsnag';
import type {
  IMapboxSearchRetrieveOptions,
  IMapboxSearchSuggestOptions,
  SearchBoxRetrieveResponse,
  SearchBoxSuggestion,
} from '@/types/mapbox';
import { DEFAULT_SEARCH_TYPES, EXCLUDED_SEARCH_FEATURE_TYPES, MAPBOX_API } from '../constants';

/**
 * Fetch search suggestions from Mapbox Search Box API
 *
 * @see https://docs.mapbox.com/api/search/search-box/#search-box-api
 *
 * @param options - Search suggest options
 * @param signal - Optional AbortSignal for cancelling the request
 * @returns Promise resolving to array of suggestions
 *
 * @example
 * ```typescript
 * const suggestions = await fetchSearchSuggestions({
 *   query: 'Gare du Nord',
 *   sessionToken: 'unique-session-id',
 *   accessToken: 'pk.ey...',
 *   limit: 10,
 * });
 * ```
 */
export async function fetchSearchSuggestions(
  options: IMapboxSearchSuggestOptions,
  signal?: AbortSignal,
): Promise<SearchBoxSuggestion[]> {
  try {
    const { query, sessionToken, accessToken, limit = 10, types = DEFAULT_SEARCH_TYPES } = options;

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('access_token', accessToken);
    params.set('session_token', sessionToken);
    params.set('limit', String(limit));
    params.set('types', types.join(','));

    const url = `${MAPBOX_API.SEARCH_SUGGEST}?${params.toString()}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      toast.warning(`Mapbox suggest error: ${text}`);
      return [];
    }

    const json = await res.json();
    const rawSuggestions: SearchBoxSuggestion[] = Array.isArray(json.suggestions)
      ? json.suggestions
      : (json.suggestions ?? json);

    const filteredSuggestions = filterSuggestionsByFeatureType(
      rawSuggestions,
      EXCLUDED_SEARCH_FEATURE_TYPES,
    );

    return filteredSuggestions;
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      return [];
    }
    // The raw provider message never reaches the user; keep it for Bugsnag.
    notifyError(error, { context: 'mapbox:search:suggest' });
    toast.error('Suggest fetch error');
    return [];
  }
}

/**
 * Retrieve full feature details from Mapbox Search Box API
 *
 * @see https://docs.mapbox.com/api/search/search-box/#retrieve-a-suggested-feature
 *
 * @param options - Retrieve options
 * @returns Promise resolving to retrieve response or undefined on error
 *
 * @example
 * ```typescript
 * const feature = await fetchSearchRetrieve({
 *   mapboxId: 'dXJuOm1ieHBvaTpkYTY4...',
 *   sessionToken: 'unique-session-id',
 *   accessToken: 'pk.ey...',
 * });
 * ```
 */
export async function fetchSearchRetrieve(
  options: IMapboxSearchRetrieveOptions,
): Promise<SearchBoxRetrieveResponse | undefined> {
  try {
    const { mapboxId, sessionToken, accessToken } = options;

    const params = new URLSearchParams();
    params.set('access_token', accessToken);
    params.set('session_token', sessionToken);

    const url = `${MAPBOX_API.SEARCH_RETRIEVE}/${String(mapboxId)}?${params.toString()}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      const text = await res.text();
      toast.error(`Mapbox retrieve error: ${text}`);
      return undefined;
    }

    const json: SearchBoxRetrieveResponse = await res.json();
    return json;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Retrieve error: ${errorMessage}`);
    return undefined;
  }
}

/**
 * Filter suggestions by excluding certain feature types
 *
 * @param items - Array of suggestions to filter
 * @param excluded - Array of feature types to exclude
 * @returns Filtered array of suggestions
 */
function filterSuggestionsByFeatureType(
  items: SearchBoxSuggestion[],
  excluded: readonly string[],
): SearchBoxSuggestion[] {
  const excludeSet = new Set(excluded.map((t) => t.toLowerCase()));
  return items.filter((s) => {
    const featureType = getFeatureType(s);
    if (!featureType) return true;
    return !excludeSet.has(featureType.toLowerCase());
  });
}

/**
 * Extract feature type from suggestion
 *
 * @param suggestion - Search box suggestion
 * @returns Feature type as string or null
 */
function getFeatureType(suggestion: SearchBoxSuggestion): string | null {
  const type = suggestion.feature_type ?? null;
  return type ? String(type) : null;
}

/**
 * Extract POI category from suggestion
 *
 * @param suggestion - Search box suggestion
 * @returns POI category as string or null
 */
export function getPoiCategory(suggestion: SearchBoxSuggestion): string | null {
  const maybe = suggestion.poi_category ?? null;
  if (!maybe) return null;
  if (Array.isArray(maybe)) return String(maybe[0]);
  return String(maybe);
}

/**
 * Group suggestions by category (Train stations, Addresses, POIs)
 *
 * @param suggestions - Array of suggestions to group
 * @returns Object with groups and ordered group keys
 */
export function groupSuggestions(suggestions: SearchBoxSuggestion[]): {
  groups: Record<string, SearchBoxSuggestion[]>;
  orderedGroupKeys: string[];
} {
  const groups: Record<string, SearchBoxSuggestion[]> = {};

  suggestions.forEach((s) => {
    const key = getGroupKey(s);
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });

  const orderedGroupKeys = ['Train stations', 'Addresses', 'POIs'];

  return { groups, orderedGroupKeys };
}

/**
 * Get the group key for a suggestion (used for grouping)
 *
 * @param suggestion - Search box suggestion
 * @returns Group key string
 */
function getGroupKey(suggestion: SearchBoxSuggestion): string {
  const category = getPoiCategory(suggestion);

  if (
    category?.toLowerCase().includes('train station') ||
    category?.toLowerCase().includes('transportation')
  ) {
    return 'Train stations';
  }

  return 'Addresses';
}
